import React, { useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import Button from '../../../elements/button'
import Text from '../../../elements/Typo/Text'
import { ColumnRename } from '../utils/columnNameStandardizer'
import HighlightedColumnName from './HighlightedColumnName'

interface Props {
    renames: ColumnRename[] | null
    onReupload: () => void
}

const NOT_PERMITTED_CHARACTERS = ['!', '"', '#', '$', '%', '&', "'", '(', ')', '*', '+', ',', '-', '.', '/', ':', ';', '=', '>', '?', '@', '[', '\\', ']', '^', '`', '{', '|', '}', '~']

const CsvColumnNameBlockDialog: React.FC<Props> = ({ renames, onReupload }) => {
    const { t } = useTranslation()
    const dialogRef = useRef<HTMLDialogElement>(null)

    useEffect(() => {
        const dialog = dialogRef.current
        if (!dialog) return
        if (renames && !dialog.open) dialog.showModal()
        else if (!renames && dialog.open) dialog.close()
    }, [renames])

    return (
        <dialog
            data-testid="csv-column-name-block-dialog"
            ref={dialogRef}
            className="modal"
            onCancel={(e) => {
                e.preventDefault()
                onReupload()
            }}
        >
            <div className="modal-box font-sans !p-4 !max-w-[40rem] !w-[40rem]">
                <div className="flex flex-col gap-3 text-[0.6rem] leading-[1.35]">
                    <div className="bg-red-100 -mx-4 px-4 -mt-4 py-2 border-b border-red-200 rounded-t-md">
                        <Text size="lg" className="font-semibold !text-red-800">
                            {t('Data Validation Failed: Unsupported Characters Detected in Column Names')}
                        </Text>
                    </div>

                    <div className="flex flex-col gap-1">
                        <p>{t('We detected {{count}} column name(s) containing one or more unsupported special characters.', { count: renames?.length ?? 0 })}</p>
                        <p>
                            {t(
                                'To ensure successful data validation and downstream processing, column names must not contain unsupported special characters. Please update the affected column names in your source file and re-upload the file before proceeding.',
                            )}
                        </p>
                        <p>{t('For your convenience, we have provided a list of affected column names along with recommended replacements below.')}</p>
                    </div>

                    <Text size="md" className="font-semibold">
                        {t('Column Name Mapping')}
                    </Text>
                    <div className="max-h-[16rem] overflow-auto border border-slate-200 rounded-md">
                        <table className="w-full text-left">
                            <thead className="bg-slate-100 sticky top-0">
                                <tr>
                                    <th className="px-2 py-1 font-semibold">{t('Original Column Name')}</th>
                                    <th className="px-2 py-1 font-semibold">{t('Recommended Column Name')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {renames?.map((rename) => (
                                    <tr key={rename.original} className="border-t border-slate-200">
                                        <td className="px-2 py-1 break-all">
                                            <HighlightedColumnName name={rename.original} />
                                        </td>
                                        <td className="px-2 py-1 break-all font-medium">{rename.standardized}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-md p-2">
                        <p className="font-semibold">{t('Note')}</p>
                        <p>
                            <Trans i18nKey="The following special characters are <bold>not permitted</bold> in column names and must be removed or replaced before re-uploading the file:" components={{ bold: <strong /> }} />
                        </p>
                        <ul className="flex flex-wrap gap-1 mt-2" aria-label={t('Not permitted characters')}>
                            {NOT_PERMITTED_CHARACTERS.map((char) => (
                                <li key={char}>
                                    <kbd className="inline-flex items-center justify-center min-w-[1.25rem] h-[1.25rem] px-1 font-mono text-[0.6rem] text-slate-800 bg-white border border-slate-300 rounded shadow-sm">{char}</kbd>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="flex justify-end gap-2 mt-2">
                        <Button theme="primary" size="xs" onClick={onReupload}>
                            {t('Reupload Data with Corrected Column Names')}
                        </Button>
                    </div>
                </div>
            </div>
        </dialog>
    )
}

export default CsvColumnNameBlockDialog
